import { useState, useEffect, useMemo, useCallback } from 'react'
import { motion } from 'framer-motion'
import { Link } from 'react-router-dom'
import { Button } from '@mui/material'
import AddIcon from '@mui/icons-material/Add'
import { useAuth } from '../components/LaunchGate'
import SearchBar from '../components/SearchBar'
import Tag from '../components/Tag'
import BlogCard from '../components/BlogCard'
import SEO from '../utils/seo'
import { colors } from '../styles/theme'

const API_URL = import.meta.env.VITE_API_URL || '/api'

const BlogListing = () => {
  const { isContributor } = useAuth()
  const [posts, setPosts] = useState([])
  const [searchQuery, setSearchQuery] = useState('')
  const [selectedTags, setSelectedTags] = useState([])
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    fetchPosts()
  }, [])

  const fetchPosts = async () => {
    try {
      const response = await fetch(`${API_URL}/blog-posts?status=published`)
      const data = await response.json()
      setPosts(data)
    } catch (error) {
      console.error('Error fetching blog posts:', error)
    } finally {
      setLoading(false)
    }
  }

  const allTags = useMemo(() => {
    const tags = new Set()
    posts.forEach(post => post.tags.forEach(tag => tags.add(tag)))
    return Array.from(tags).sort()
  }, [posts])

  const filteredPosts = useMemo(() => {
    let results = posts
    if (searchQuery) {
      const q = searchQuery.toLowerCase()
      results = results.filter(p =>
        p.title.toLowerCase().includes(q) ||
        p.excerpt.toLowerCase().includes(q) ||
        p.tags.some(t => t.toLowerCase().includes(q))
      )
    }
    if (selectedTags.length > 0) {
      results = results.filter(p =>
        selectedTags.every(st => p.tags.includes(st))
      )
    }
    return results
  }, [posts, searchQuery, selectedTags])

  const handleSearch = useCallback((query) => {
    setSearchQuery(query)
  }, [])

  const toggleTag = (tag) => {
    setSelectedTags(prev =>
      prev.includes(tag)
        ? prev.filter(t => t !== tag)
        : [...prev, tag]
    )
  }

  return (
    <>
      <SEO
        title="Blog"
        description="Thoughts on AI, app development, developer experience, and data — from the Prompted Thoughts team."
      />

      <div className="section">
        <div className="container-custom">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
          >
            <div className="flex items-center justify-between mb-8">
              <div>
                <h1 className="text-4xl md:text-5xl font-bold text-off-white">Blog</h1>
                <p className="text-gray-400 mt-2">
                  {filteredPosts.length} {filteredPosts.length === 1 ? 'post' : 'posts'}
                </p>
              </div>
              {isContributor && (
                <Link to="/blog/editor">
                  <Button
                    variant="contained"
                    startIcon={<AddIcon />}
                    sx={{
                      backgroundColor: colors.accent,
                      color: colors.textOnAccent,
                      fontWeight: 700,
                      '&:hover': { backgroundColor: colors.accentHover },
                    }}
                  >
                    Write
                  </Button>
                </Link>
              )}
            </div>

            <div className="mb-8">
              <SearchBar onSearch={handleSearch} placeholder="Search blog posts..." />
            </div>

            {allTags.length > 0 && (
              <div className="flex flex-wrap gap-2 mb-8">
                {allTags.map(tag => (
                  <Tag
                    key={tag}
                    tag={tag}
                    clickable
                    selected={selectedTags.includes(tag)}
                    onClick={() => toggleTag(tag)}
                  />
                ))}
              </div>
            )}

            {loading ? (
              <div className="text-center py-20">
                <p className="text-gray-400">Loading posts...</p>
              </div>
            ) : filteredPosts.length > 0 ? (
              <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
                {filteredPosts.map((post, index) => (
                  <BlogCard key={post.slug} post={post} index={index} />
                ))}
              </div>
            ) : (
              <div className="text-center py-20">
                <p className="text-gray-400 text-lg mb-4">
                  {posts.length === 0 ? 'No blog posts yet.' : 'No posts match your search.'}
                </p>
                {posts.length === 0 && isContributor && (
                  <Link to="/blog/editor">
                    <Button
                      variant="outlined"
                      sx={{
                        borderColor: colors.accent, color: colors.accent,
                        '&:hover': { borderColor: colors.accent, backgroundColor: 'rgba(var(--color-accent-primary-rgb), 0.1)' },
                      }}
                    >
                      Write your first post
                    </Button>
                  </Link>
                )}
              </div>
            )}
          </motion.div>
        </div>
      </div>
    </>
  )
}

export default BlogListing
